/**
 * HasPermissionDirective - Conditionally show/hide UI elements based on permissions
 *
 * Purpose: Hide UI elements user doesn't have permission to access.
 * Prevents users from seeing buttons/links they can't use.
 *
 * Usage:
 *
 * With single permission:
 * <button *appHasPermission="'MANAGE_USERS'">Manage Users</button>
 *
 * With permission enum:
 * <button *appHasPermission="Permission.MANAGE_USERS">Manage Users</button>
 *
 * With role check:
 * <button *appHasRole="'admin'">Admin Panel</button>
 *
 * With minimum role level:
 * <button *appHasRoleLevel="'admin'">Admin Features</button>
 *
 * With multiple permissions (any):
 * <button *appHasAnyPermission="[Permission.MANAGE_USERS, Permission.DELETE_USERS]">
 *   User Actions
 * </button>
 *
 * With multiple permissions (all):
 * <button *appHasAllPermissions="[Permission.DELETE_COMPANY, Permission.MANAGE_PAYMENT]">
 *   Dangerous Operation
 * </button>
 *
 * Inverted (show when user DOESN'T have permission):
 * <div *appHasPermission="'MANAGE_USERS'; else: true">
 *   <p>You don't have permission to manage users.</p>
 * </div>
 */
import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PermissionService, Permission } from '../services/permission.service';

/**
 * Directive for permission-based element visibility
 */
@Directive({
  selector: '[appHasPermission]',
  standalone: false,
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private permissionService = inject(PermissionService);
  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);
  private destroy$ = new Subject<void>();

  private permission?: Permission | string;
  private isInverted = false;
  private hasView = false;

  @Input()
  set appHasPermission(permission: Permission | string) {
    this.permission = permission;
    this.updateView();
  }

  @Input()
  set appHasPermissionElse(inverted: boolean) {
    this.isInverted = inverted;
    this.updateView();
  }

  constructor() {}

  ngOnInit() {
    // Subscribe to user changes to update view reactively
    this.permissionService.isAuthenticatedObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView() {
    if (!this.permission) {
      return;
    }

    // Convert string to Permission enum if needed
    const permissionValue = typeof this.permission === 'string'
      ? Permission[this.permission as keyof typeof Permission]
      : this.permission;

    const hasPermission = this.permissionService.hasPermission(permissionValue);
    const shouldShow = this.isInverted ? !hasPermission : hasPermission;

    if (shouldShow && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!shouldShow && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}

/**
 * Directive for role-based element visibility
 */
@Directive({
  selector: '[appHasRole]',
  standalone: false,
})
export class HasRoleDirective implements OnInit, OnDestroy {
  private permissionService = inject(PermissionService);
  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);
  private destroy$ = new Subject<void>();

  private role?: string;
  private hasView = false;

  @Input()
  set appHasRole(role: string) {
    this.role = role;
    this.updateView();
  }

  constructor() {}

  ngOnInit() {
    this.permissionService.isAuthenticatedObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView() {
    if (!this.role) {
      return;
    }

    const hasRole = this.permissionService.hasRole(this.role);

    if (hasRole && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasRole && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}

/**
 * Directive for minimum role level-based element visibility
 */
@Directive({
  selector: '[appHasRoleLevel]',
  standalone: false,
})
export class HasRoleLevelDirective implements OnInit, OnDestroy {
  private permissionService = inject(PermissionService);
  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);
  private destroy$ = new Subject<void>();

  private minimumRole?: string;
  private hasView = false;

  @Input()
  set appHasRoleLevel(minimumRole: string) {
    this.minimumRole = minimumRole;
    this.updateView();
  }

  constructor() {}

  ngOnInit() {
    this.permissionService.isAuthenticatedObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView() {
    if (!this.minimumRole) {
      return;
    }

    const hasRoleLevel = this.permissionService.hasRoleLevel(this.minimumRole);

    if (hasRoleLevel && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasRoleLevel && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}

/**
 * Directive for multiple permissions (any) element visibility
 */
@Directive({
  selector: '[appHasAnyPermission]',
  standalone: false,
})
export class HasAnyPermissionDirective implements OnInit, OnDestroy {
  private permissionService = inject(PermissionService);
  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);
  private destroy$ = new Subject<void>();

  private permissions?: Permission[];
  private hasView = false;

  @Input()
  set appHasAnyPermission(permissions: Permission[]) {
    this.permissions = permissions;
    this.updateView();
  }

  constructor() {}

  ngOnInit() {
    this.permissionService.isAuthenticatedObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView() {
    if (!this.permissions || this.permissions.length === 0) {
      return;
    }

    const hasAnyPermission = this.permissionService.hasAnyPermission(this.permissions);

    if (hasAnyPermission && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasAnyPermission && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}

/**
 * Directive for multiple permissions (all) element visibility
 */
@Directive({
  selector: '[appHasAllPermissions]',
  standalone: false,
})
export class HasAllPermissionsDirective implements OnInit, OnDestroy {
  private permissionService = inject(PermissionService);
  private templateRef = inject(TemplateRef<any>);
  private viewContainer = inject(ViewContainerRef);
  private destroy$ = new Subject<void>();

  private permissions?: Permission[];
  private hasView = false;

  @Input()
  set appHasAllPermissions(permissions: Permission[]) {
    this.permissions = permissions;
    this.updateView();
  }

  constructor() {}

  ngOnInit() {
    this.permissionService.isAuthenticatedObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView() {
    if (!this.permissions || this.permissions.length === 0) {
      return;
    }

    const hasAllPermissions = this.permissionService.hasAllPermissions(this.permissions);

    if (hasAllPermissions && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasAllPermissions && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
